import React from 'react';
import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import FileSaver from 'file-saver';
import { connect } from 'react-redux';

import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import RuleIcon from '@mui/icons-material/Rule';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

import LoadDesign from "../loadDesign";
import ZipProject from "../zipProject";
import Controls from "../properties/controls";
import Align from "../properties/align";
import { newDesign, importDesign, clearRowSelection } from "../../actions";
import { getSelectedRowContent, isImage, isText, isIf, isIfElse, isMjml } from "../../selectors";

const SIDEBAR_WIDTH = 56;

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    alignSelf: 'flex-start',
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  panel: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    overflowX: 'hidden',
    borderRadius: 0,
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0),
    flexShrink: 0,
  },
  editGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0),
  },
}));

function getTypeLabel({ contentIsImage, contentIsText, contentIsIf, contentIsIfElse, contentIsMjml }) {
  if (contentIsImage) return "Image";
  if (contentIsText) return "Text";
  if (contentIsIf) return "If statement";
  if (contentIsIfElse) return "If Else statement";
  if (contentIsMjml) return "MJML Block";
  return "";
}

function EditTypeIcon({ contentIsImage, contentIsText, contentIsMjml }) {
  if (contentIsImage) return <ImageIcon />;
  if (contentIsText || contentIsMjml) return <CodeIcon />;
  return <RuleIcon />;
}

const ActionSidebar = ({
  design, selectedRowContent, contentIsImage, contentIsText, contentIsIf, contentIsIfElse, contentIsMjml,
  onCreateNewDesign, onImportDesign, onClearSelection,
  editorOpen, onToggleEditor, onSave, onReset,
  theme, onToggleTheme,
}) => {
  const classes = useStyles();
  const [importError, setImportError] = React.useState(false);
  const importFileRef = React.useRef(null);

  function exportDesign() {
    const blob = new Blob([JSON.stringify(design)], { type: "text/plain;charset=utf-8" });
    FileSaver.saveAs(blob, "design.json.txt");
  }

  function handleImportClick() {
    importFileRef.current.value = '';
    importFileRef.current.click();
  }

  function handleImportFile(event) {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!parsed.header || !parsed.body || !parsed.footer) throw new Error('Invalid structure');
        onImportDesign(parsed);
      } catch (_) {
        setImportError(true);
      }
    };
    reader.readAsText(file);
  }

  function handleImportErrorClose() {
    setImportError(false);
  }

  const typeLabel = getTypeLabel({ contentIsImage, contentIsText, contentIsIf, contentIsIfElse, contentIsMjml });

  return (
    <div className={classes.wrapper}>
      <Paper square className={classes.panel}>
        <Tooltip title="W3HTech - HTML Email Template Generator" placement="right">
          <IconButton
            component="a"
            href="https://w3htech.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="W3HTech - HTML Email Template Generator"
            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
          >
            W3H
          </IconButton>
        </Tooltip>
        <Divider flexItem />

        <div className={classes.actionBar}>
          <Tooltip title="New" placement="right">
            <IconButton aria-label="New" onClick={onCreateNewDesign}>
              <AddIcon />
            </IconButton>
          </Tooltip>

          <LoadDesign iconOnly />

          <Tooltip title="Load JSON" placement="right">
            <IconButton aria-label="Load JSON" onClick={handleImportClick}>
              <FileUploadIcon />
            </IconButton>
          </Tooltip>
          <input
            type="file"
            accept=".json,.txt"
            ref={importFileRef}
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />

          <Tooltip title="Export" placement="right">
            <IconButton aria-label="Export" onClick={exportDesign}>
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          <ZipProject iconOnly />
        </div>

        <Divider flexItem />

        <div className={classes.actionBar}>
          <Tooltip title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'} placement="right">
            <IconButton
              aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
              onClick={onToggleTheme}
            >
              {theme === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
        </div>

        {selectedRowContent && (
          <>
            <Divider flexItem />
            <div className={classes.editGroup}>
              <Controls />
              <Align content={selectedRowContent} />

              <Tooltip title={`Edit ${typeLabel}`} placement="right">
                <IconButton
                  aria-label={`Edit ${typeLabel}`}
                  color={editorOpen ? "secondary" : "default"}
                  onClick={onToggleEditor}
                >
                  {typeLabel ? (
                    <EditTypeIcon contentIsImage={contentIsImage} contentIsText={contentIsText} contentIsMjml={contentIsMjml} />
                  ) : (
                    <EditIcon />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="Save" placement="right">
                <IconButton aria-label="Save" onClick={onSave}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Reset" placement="right">
                <IconButton aria-label="Reset" onClick={onReset}>
                  <RestoreIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Deselect element" placement="right">
                <IconButton aria-label="Deselect element" onClick={onClearSelection}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </>
        )}
      </Paper>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={importError}
        onClose={handleImportErrorClose}
        autoHideDuration={4000}
        message="Failed to load JSON — file must be a valid exported design"
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleImportErrorClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    design: {
      header: state.design.header,
      body: state.design.body,
      footer: state.design.footer,
      id: state.design.id
    },
    selectedRowContent: getSelectedRowContent(state),
    contentIsImage: isImage(state),
    contentIsText: isText(state),
    contentIsIf: isIf(state),
    contentIsIfElse: isIfElse(state),
    contentIsMjml: isMjml(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onCreateNewDesign: () => {
      dispatch(newDesign());
    },
    onImportDesign: (design) => {
      dispatch(importDesign(design));
    },
    onClearSelection: () => {
      dispatch(clearRowSelection());
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ActionSidebar);
